import { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { ActivityIndicator } from '@trinity/chat-components/components/presentation/ActivityIndicator.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { cx } from '@trinity/chat-components/utils';
import { ResponseQuestion } from '@/components/presentation/ProjectSurveyResponses';
import { QuestionResponses } from '@/components/SurveyResponses';
import { useProjectOrLatestChild } from '@/containers/GroupProject/hooks';
import { SurveyResponsesContainer } from '@/containers/GroupProject/SurveyResponsesContainer';
import { useSurvey } from '@/containers/Survey/hooks';
import { useProjectSurveyResponsesLoading, useProjectSurveyResponsesState, useValidateResponsesPath } from '@/containers/SurveyResponses/hooks';
import { QuestionResponsesContainer } from '@/containers/SurveyResponses/QuestionResponsesContainer';
import styles from './style/Project.Responses.module.css';

type Props = unknown;

export const ProjectResponses = (props: Props) => {
  const project = useProjectOrLatestChild();
  return (
    <SurveyResponsesContainer projectId={project.id}>
      <ProjectSurveyResponses ResponsesComponent={QuestionResponsesContainer} />
    </SurveyResponsesContainer>
  );
};

const useQuestionsScrollRef = () => {
  const ctx = useProjectSurveyResponsesState();
  const loading = useProjectSurveyResponsesLoading();

  const [mounted, setMounted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mounted && ref.current) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setMounted(true);
    }
  }, [
    ctx.query.questionId,
    loading,
    mounted,
    setMounted,
  ]);

  return ref;
};

const useResponsesScrollRef = () => {
  const ctx = useProjectSurveyResponsesState();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo({
        behavior: 'auto',
        top: 0,
      });
    }
  }, [
    ctx.query.questionId,
    // @ts-ignore
    ctx.query.page,
  ]);

  return ref;
};

type ProjectResponsesProps = {
  ResponsesComponent?: React.ComponentType;
};

const ProjectSurveyResponses = ({
  ResponsesComponent = QuestionResponses,
}: ProjectResponsesProps) => {
  const ctx = useProjectSurveyResponsesState();
  const loading = useProjectSurveyResponsesLoading();
  const survey = useSurvey();
  const validatePath = useValidateResponsesPath();
  const appThemingContext = useContext(AppThemingContext);

  useEffect(validatePath, [validatePath]);
  const questionsScrollRef = useQuestionsScrollRef();
  const responsesScrollRef = useResponsesScrollRef();

  const renderQuestions = useCallback(() => {
    if (!survey) {
      return null;
    }

    return survey.questions
      .sort((a, b) => a.ordinal - b.ordinal)
      .map(question => {
        const isActive = question.base.id === ctx.query.questionId;
        const ref = isActive ? questionsScrollRef : undefined;

        // Apply theming styles if enabled
        const themeStyles = appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
          ? {
            style: {
              '--theme-color': appThemingContext.palette.primary.main,
              '--theme-color-20': `${appThemingContext.palette.primary.main}20`, // 20% opacity
            } as React.CSSProperties,
          }
          : {};

        return (
          <Link
            replace
            key={question.base.id}
            to={`${ctx.basePath}?questionId=${question.base.id}&surveyId=${survey.surveyId}`}
            {...themeStyles}>
            <ResponseQuestion
              ref={ref}
              isActive={isActive}
              item={question} />
          </Link>
        );
      });
  }, [
    ctx?.basePath,
    ctx?.query?.questionId,
    questionsScrollRef,
    survey,
    appThemingContext,
  ]);

  const renderResponses = useCallback(() => {
    if (!ctx?.query?.questionId) {
      return null;
    }

    return (
      <ResponsesComponent />
    );
  }, [
    ctx?.query?.questionId,
    ResponsesComponent,
  ]);

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <div className={cx(styles.main, { [styles.browsing]: !!ctx.query.questionId })}>

          <div className={cx(styles.section, styles.scroll)}>
            <div className={styles.header}>
              <h2 className={styles.h2}>Questions</h2>
            </div>

            {renderQuestions()}
          </div>

          <div className={styles.section}>
            <div
              className={styles.scroll}
              ref={responsesScrollRef}>
              <div className={styles.header}>
                {ctx.query.questionId &&
                  <Link
                    className={styles.back}
                    to={ctx.basePath}
                    style={appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
                      ? { color: appThemingContext.palette.primary.main }
                      : {}}>
                    <ChevronLeftRoundedIcon
                      fontSize="large"
                      style={appThemingContext?.enabled && appThemingContext?.palette?.primary?.main
                        ? { color: appThemingContext.palette.primary.main }
                        : {}} />
                  </Link>
                }
                <h2 className={styles.h2}>Responses</h2>
              </div>

              {renderResponses()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};